import { Stack, Theme } from '@mui/material';
import { useState } from 'react';
import { useTodos } from '../../../../context/TodoContext/TodoContext';
import { Todo } from '../../../../types/types';
import TodoPopupMenu from '../../../PopupMenu/TodoPopupMenu';
import { StyledEventItem } from '../../styles/styles';
import { CheckboxButton } from './components/CheckboxButton';
import { EditableContent } from './components/EditableContent';
import { TimeDisplay } from './components/TimeDisplay';
import { useEventItem } from './hooks/useEventItem';

interface EventItemProps {
    todo: Todo;
    color: string;
    onClick: () => void;
    showTime: boolean;
}

const EventItem: React.FC<EventItemProps> = ({ todo, color, onClick, showTime }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const {
        isEditing,
        editText,
        handleEdit,
        handleSave,
        handleKeyPress,
        handleTextChange,
        inputRef
    } = useEventItem(todo);

    const { deleteTodo, toggleTodo } = useTodos();

    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleDelete = () => {
        deleteTodo(todo.id);
        handleClose();
    };

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleTodo(todo.id);
    };

    return (
        <>
            <StyledEventItem
                bgcolor={color}
                onClick={isEditing ? undefined : onClick}
                onContextMenu={handleRightClick}
                isEditing={isEditing}
                onDoubleClick={handleEdit}
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    opacity: (todo.completed && !isEditing) ? 0.75 : "auto",
                    ...((Boolean(anchorEl) || isEditing) && {
                        boxShadow: (theme: Theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                        transform: isEditing ? 'none' : 'scale(1.02)',
                        scale: isEditing ? '1.02' : "auto",
                        zIndex: 1,
                    }),
                }}
            >
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
                    <CheckboxButton
                        completed={todo.completed}
                        onToggle={handleToggle}
                    />

                    <EditableContent
                        isEditing={isEditing}
                        text={todo.text}
                        editText={editText}
                        completed={todo.completed}
                        inputRef={inputRef}
                        onTextChange={handleTextChange}
                        onKeyPress={handleKeyPress}
                        onSave={handleSave}
                    />
                </Stack>

                <TimeDisplay
                    dueDate={todo.dueDate}
                    showTime={showTime}
                />
            </StyledEventItem>

            <TodoPopupMenu
                anchorEl={anchorEl}
                onClose={handleClose}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />
        </>
    );
};

export default EventItem; 